import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Función para renovar token
async function refreshToken(user) {
  try {
    const token = await user.getIdToken(true); // Force refresh
    localStorage.setItem('authToken', token);
    return token;
  } catch (error) {
    console.error('❌ Error renovando token:', error);
    throw error;
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  async function signup(email, password, username, influencerCode = null) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Actualizar perfil con username
      await updateProfile(userCredential.user, {
        displayName: username
      });

      // Crear documento de usuario en Firestore
      const userData = {
        email,
        username,
        createdAt: new Date(),
        estado: 'activo',
        plan: 'basico', // Cambiado de 'gratuito' a 'basico'
        onboardingCompletado: false,
        configuracion: {
          partidos: [],
          umbrales: {},
          frecuencia: 60 // 60 minutos por defecto
        }
      };

      // CÓDIGO DE INFLUENCER COMENTADO - Se maneja en la pantalla de pago
      // El código de influencer se aplica cuando el usuario ingresa el cupón
      // en la pantalla previa al pago con Stripe, no durante el registro
      /*
      if (influencerCode) {
        userData.influencerCode = influencerCode;
        userData.referredBy = influencerCode;
      }
      */

      await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      // Establecer el perfil en memoria inmediatamente para evitar esperas/race conditions
      setUserProfile(userData);

      // Nota: ya no creamos referidos aquí; se crean tras el pago exitoso via webhook

      // Obtener y guardar el token ID
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);

      toast.success('¡Cuenta creada exitosamente!');
      return userCredential;
    } catch (error) {
      console.error('Error en signup:', error);
      let errorMessage = 'Error al crear la cuenta';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Este correo ya está registrado';
          break;
        case 'auth/weak-password':
          errorMessage = 'La contraseña debe tener al menos 6 caracteres';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Correo electrónico inválido';
          break;
        default:
          errorMessage = 'Error al crear la cuenta';
          break;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Obtener y guardar el token ID
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);
      
      toast.success('¡Inicio de sesión exitoso!');
      return userCredential;
    } catch (error) {
      console.error('Error en login:', error);
      let errorMessage = 'Error al iniciar sesión';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Usuario no encontrado';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Contraseña incorrecta';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Correo electrónico inválido';
          break;
        default:
          errorMessage = 'Error al iniciar sesión';
          break;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      setUserProfile(null);
      localStorage.removeItem('authToken');
      toast.success('Sesión cerrada exitosamente');
    } catch (error) {
      console.error('Error en logout:', error);
      toast.error('Error al cerrar sesión');
    }
  }

  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Error enviando correo de recuperación:', error);
      let errorMessage = 'Error al enviar correo de recuperación';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No existe una cuenta con este correo electrónico';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Correo electrónico inválido';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Demasiados intentos. Intenta más tarde';
          break;
        default:
          errorMessage = 'Error al enviar correo de recuperación';
          break;
      }
      
      throw new Error(errorMessage);
    }
  }

  async function signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Verificar si es usuario nuevo
      const isNewUser = result.additionalUserInfo?.isNewUser;
      
      if (isNewUser) {
        // Crear perfil en Firestore para usuario nuevo
        const userData = {
          email: result.user.email,
          username: result.user.displayName || result.user.email.split('@')[0],
          createdAt: new Date(),
          estado: 'activo',
          plan: 'basico',
          onboardingCompletado: false,
          configuracion: {
            partidos: [],
            umbrales: {},
            frecuencia: 60
          }
        };

        await setDoc(doc(db, 'users', result.user.uid), userData);
        
        // Establecer el perfil en memoria
        setUserProfile(userData);
        
        toast.success('¡Cuenta creada con Google exitosamente!');
      } else {
        toast.success('¡Inicio de sesión con Google exitoso!');
      }
      
      // Obtener y guardar el token ID
      const token = await result.user.getIdToken();
      localStorage.setItem('authToken', token);
      
      return result;
    } catch (error) {
      console.error('Error en Google Sign-In:', error);
      let errorMessage = 'Error al iniciar sesión con Google';
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Inicio de sesión cancelado';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup bloqueado. Permite popups para este sitio';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Solicitud cancelada';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'Ya existe una cuenta con este email usando otro método';
          break;
        default:
          errorMessage = 'Error al iniciar sesión con Google';
          break;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  }

  async function getUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error obteniendo perfil:', error);
      return null;
    }
  }

  async function updateUserProfile(uid) {
    try {
      const profile = await getUserProfile(uid);
      setUserProfile(profile);
      return profile;
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      return null;
    }
  }

  // Función para manejar actualización post-pago
  async function handlePostPaymentUpdate(uid) {
    try {
      // Renovar token para asegurar que esté vigente
      if (currentUser) {
        await refreshToken(currentUser);
      }
      
      // Actualizar perfil
      const profile = await updateUserProfile(uid);
      
      return profile;
    } catch (error) {
      console.error('Error actualizando perfil post-pago:', error);
      return null;
    }
  }

  // Función para renovar token cuando sea necesario
  const refreshTokenIfNeeded = useCallback(async () => {
    if (currentUser) {
      try {
        await refreshToken(currentUser);
      } catch (error) {
        console.error('Error renovando token:', error);
        // Si falla la renovación, hacer logout
        await logout();
      }
    }
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Obtener y guardar el token ID si no existe
        if (!localStorage.getItem('authToken')) {
          const token = await user.getIdToken();
          localStorage.setItem('authToken', token);
        }
        
        // Intentar obtener el perfil del usuario
        const profile = await getUserProfile(user.uid);
        
        // Si no hay perfil, crear uno básico para usuarios de Google
        if (!profile) {
          console.log('🔍 No se encontró perfil, creando perfil básico para usuario Google');
          const basicProfile = {
            email: user.email,
            username: user.displayName || user.email.split('@')[0],
            createdAt: new Date(),
            estado: 'activo',
            plan: 'basico',
            onboardingCompletado: false,
            configuracion: {
              partidos: [],
              umbrales: {},
              frecuencia: 60
            }
          };
          
          try {
            await setDoc(doc(db, 'users', user.uid), basicProfile);
            setUserProfile(basicProfile);
            console.log('✅ Perfil básico creado para usuario Google');
          } catch (error) {
            console.error('❌ Error creando perfil básico:', error);
            setUserProfile(null);
          }
        } else {
          setUserProfile(profile);
        }
      } else {
        setUserProfile(null);
        localStorage.removeItem('authToken');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Renovar token cada 50 minutos (los tokens expiran en 1 hora)
  useEffect(() => {
    if (currentUser) {
      const tokenRefreshInterval = setInterval(() => {
        refreshTokenIfNeeded();
      }, 50 * 60 * 1000); // 50 minutos

      return () => clearInterval(tokenRefreshInterval);
    }
  }, [currentUser, refreshTokenIfNeeded]);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    getUserProfile,
    updateUserProfile,
    handlePostPaymentUpdate,
    refreshTokenIfNeeded
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
